(() => {
  if (window.__awVoicePickupLoaded) {
    return;
  }

  window.__awVoicePickupLoaded = true;

  const instances = new Map();
  const sliderUtils = window.AWSliderCoreUtils;
  const selectors = {
    root: "[data-aw-voice-pickup]",
    viewport: "[data-aw-voice-pickup-viewport]",
    track: "[data-aw-voice-pickup-track]",
    item: "[data-aw-voice-pickup-item]",
    scrollbar: "[data-aw-voice-pickup-scrollbar]",
    thumb: "[data-aw-voice-pickup-thumb]",
    prev: "[data-aw-voice-pickup-prev]",
    next: "[data-aw-voice-pickup-next]",
  };

  class AWVoicePickup {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.viewport = root.querySelector(selectors.viewport);
      this.track = root.querySelector(selectors.track);
      this.items = Array.from(root.querySelectorAll(selectors.item));
      this.scrollbar = root.querySelector(selectors.scrollbar);
      this.thumb = root.querySelector(selectors.thumb);
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.rafId = null;
      this.teardownResizeObserver = null;

      this.onScroll = this.requestUpdate.bind(this);
      this.onResize = this.update.bind(this);
      this.onPrevClick = this.scrollPrev.bind(this);
      this.onNextClick = this.scrollNext.bind(this);
      this.onImageLoad = this.requestUpdate.bind(this);

      this.bindEvents();
      this.update();
    }

    bindEvents() {
      if (this.viewport) {
        this.viewport.addEventListener("scroll", this.onScroll, { passive: true });
      }

      if (this.prevButton) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", this.onNextClick);
      }

      this.teardownResizeObserver = sliderUtils.observeResize(
        [this.root, this.viewport, this.track],
        this.onResize
      );

      sliderUtils.bindImageLoad(this.root, this.onImageLoad);
    }

    getMaxScroll() {
      return sliderUtils.getMaxScroll(this.viewport);
    }

    isMobileLayout() {
      return window.innerWidth <= 1099;
    }

    getScrollAmount() {
      return sliderUtils.getItemDistance({
        list: this.track,
        items: this.items,
        itemSelector: selectors.item,
        viewport: this.viewport,
      });
    }

    getLastItemLeft() {
      const lastItem = this.items[this.items.length - 1];

      if (!lastItem) {
        return 0;
      }

      return lastItem.offsetLeft;
    }

    syncDesktopEndPadding() {
      if (!this.track || !this.viewport) {
        return;
      }

      if (this.isMobileLayout()) {
        this.track.style.paddingRight = "";
        return;
      }

      const lastItem = this.items[this.items.length - 1];

      if (!lastItem) {
        this.track.style.paddingRight = "";
        return;
      }

      const padRight = Math.max(0, this.viewport.clientWidth - lastItem.getBoundingClientRect().width);
      this.track.style.paddingRight = `${padRight}px`;
    }

    requestUpdate() {
      sliderUtils.requestFrame(this, "rafId", () => this.update());
    }

    scrollPrev() {
      if (!this.viewport) {
        return;
      }

      this.syncDesktopEndPadding();

      const maxScroll = this.getMaxScroll();

      if (maxScroll <= 1) {
        return;
      }

      if (!this.isMobileLayout() && this.viewport.scrollLeft <= 1) {
        this.viewport.scrollTo({
          left: this.getLastItemLeft(),
          behavior: sliderUtils.getScrollBehavior(),
        });
        return;
      }

      this.viewport.scrollBy({
        left: this.getScrollAmount() * -1,
        behavior: sliderUtils.getScrollBehavior(),
      });
    }

    scrollNext() {
      if (!this.viewport) {
        return;
      }

      const maxScroll = this.getMaxScroll();
      const current = Math.max(0, this.viewport.scrollLeft);

      if (maxScroll <= 1) {
        return;
      }

      if (!this.isMobileLayout() && current >= maxScroll - 1) {
        this.viewport.scrollTo({
          left: 0,
          behavior: sliderUtils.getScrollBehavior(),
        });
        return;
      }

      this.viewport.scrollBy({
        left: this.getScrollAmount(),
        behavior: sliderUtils.getScrollBehavior(),
      });
    }

    update() {
      if (!this.viewport) {
        return;
      }

      this.syncDesktopEndPadding();

      const maxScroll = this.getMaxScroll();
      const current = Math.max(0, this.viewport.scrollLeft);
      const hasOverflow = maxScroll > 1;
      const useLoopControls = hasOverflow && !this.isMobileLayout();

      sliderUtils.updateButtonState(this.prevButton, !hasOverflow || (!useLoopControls && current <= 1));
      sliderUtils.updateButtonState(this.nextButton, !hasOverflow || (!useLoopControls && current >= maxScroll - 1));

      if (this.scrollbar) {
        this.scrollbar.classList.toggle("is-hidden", !hasOverflow);
      }

      sliderUtils.updateScrollbar({
        viewport: this.viewport,
        scrollbar: this.scrollbar,
        thumb: this.thumb,
        enabled: hasOverflow,
      });
    }

    selectBlock(blockId, fallbackTarget) {
      sliderUtils.selectBlockItem({
        root: this.root,
        viewport: this.viewport,
        itemSelector: selectors.item,
        blockId,
        fallbackTarget,
      });

      this.requestUpdate();
    }

    destroy() {
      sliderUtils.cancelFrame(this, "rafId");

      if (this.viewport) {
        this.viewport.removeEventListener("scroll", this.onScroll);
      }

      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this.onNextClick);
      }

      if (this.teardownResizeObserver) {
        this.teardownResizeObserver();
        this.teardownResizeObserver = null;
      }

      sliderUtils.unbindImageLoad(this.root, this.onImageLoad);
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWVoicePickup(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId, event.target);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
